/* ---------------------------------------------------------------------
   Per-route metadata.

   Before Phase 5 all eight routes served one identical <head>: one title,
   one description, no <link rel="canonical"> anywhere, and og:url /
   og:image pointing at a Cloud Run host that is not even the current one
   (vercel.json rewrites to keystone-api-yhicwzbyja). The referenced
   images/og-preview.png did not exist, so every share card on X, LinkedIn,
   Slack and iMessage rendered imageless.

   SITE_ORIGIN: set VITE_SITE_ORIGIN at build time to the public origin.
   Without it we fall back to the runtime origin, which is always correct
   for canonical but is not visible to scrapers that do not run JS - so
   the build-time value is what makes share cards resolve. The search-index
   build plugin also uses ROUTE_META to emit each route's initial head and
   the canonical public sitemap, before any browser JavaScript runs.
   --------------------------------------------------------------------- */
export const SITE_ORIGIN = (
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_ORIGIN) ||
    (typeof window !== 'undefined' ? window.location.origin : '')
).replace(/\/+$/, '');

export const OG_IMAGE_PATH = '/images/og-preview.png';

export const ROUTE_META = {
    '/house-planner-fixed-room-dimensions': {
        title: 'House Planner with Fixed Room Dimensions - Keystone AI',
        description: 'Set bedroom, living-room and kitchen dimensions in the survey. Follow a real 14 × 12 ft bedroom example and inspect its measured floor plan.',
    },
    '/ai-floor-plan-dxf-export': {
        title: 'AI Floor Plan DXF Export: Editable CAD Example - Keystone AI',
        description: 'Download a real concept DXF with floor plans, furniture, stair risers and opening schedules. Learn its CAD layers, lineweights, units and limits.',
    },
    '/team': {
        title: 'Our Team - Keystone AI',
        description: 'Meet Sujan Acharya, Subrat Acharya and Rhythm Bhattarai: the leadership of the Keystone AI team, building floor plans and 3D home design tools.',
    },
    '/': {
        title: 'AI Floor Plan Generator & 3D Home Design - Keystone AI',
        description: 'Generate house floor plans from your room requirements, compare layouts and explore matching 3D models. Try the free studio without an account.',
    },
    '/how-floor-plans-work': {
        title: 'How Floor Plans Work - Keystone AI',
        description: 'See how Keystone AI turns a home brief into room requirements, footprint options and checked floor plans. Explore the process and concept-design limits.',
    },
    '/case-study': {
        title: 'How Floor Plans Work - Keystone AI',
        description: 'See how Keystone AI turns a home brief into room requirements, footprint options and checked floor plans. Explore the process and concept-design limits.',
        canonicalPath: '/how-floor-plans-work',
    },
    '/b2b-workflow': {
        title: 'Guided Workflow - Keystone AI',
        description: 'A guided process that gets you to the first design conversation with a real plan, elevations and a CAD export already in hand.',
    },
    '/roadmap': {
        title: 'Roadmap - Keystone AI',
        description: 'Explore current Keystone AI floor-plan tools and planned improvements. See what is available today, what is still planned and where the limits are.',
    },
    '/pricing': {
        title: 'Pricing - Keystone AI',
        description: 'Floor plans are free without an account. Compare account features and planned Pro tools. Payment checkout and photoreal generation are unavailable.',
    },
    '/account': {
        title: 'Your account - Keystone AI',
        description: 'Your saved houses, photoreal models, billing and profile.',
        noindex: true,
    },
    '/signin': {
        title: 'Sign in - Keystone AI',
        description: 'Sign in to your Keystone AI account to manage saved house designs.',
        canonicalPath: '/account',
        noindex: true,
    },
    '/faq': {
        title: 'FAQ - Keystone AI',
        description: 'Find answers about free floor plans, accounts, CAD downloads and 3D models. Understand Keystone AI concept-design limits and planned Pro features.',
    },
    '/privacy': {
        title: 'Privacy - Keystone AI',
        description: 'Read the Keystone AI privacy draft: how project inputs and generated outputs are handled, what data the service uses and how to contact the team.',
    },
    '/terms': {
        title: 'Terms - Keystone AI',
        description: 'Read the Keystone AI trial terms covering concept designs, account use and output limitations. Learn your responsibilities and how to contact the team.',
    },
};

// A canonical public route is discoverable. Account pages and duplicate aliases
// never enter the sitemap. Used by both the build and browser metadata.
export const INDEXABLE_PATHS = Object.keys(ROUTE_META).filter((path) => {
    const meta = ROUTE_META[path];
    return !meta.noindex && (!meta.canonicalPath || meta.canonicalPath === path);
});
